import React, {useState, useEffect} from 'react'
import {motion} from 'framer-motion'
import {useForm} from 'react-hook-form'
import {useSiteContent} from '../../hooks/useSiteContent'
import {useAnalytics} from '../../hooks/useAnalytics'
import AdminBreadcrumb from '../../components/admin/AdminBreadcrumb'
import SEOHead from '../../components/SEOHead'
import SafeIcon from '../../common/SafeIcon'
import LoadingSpinner from '../../components/ui/LoadingSpinner'
import toast from 'react-hot-toast'
import * as FiIcons from 'react-icons/fi'

const {FiSave, FiEye, FiPlus, FiTrash2, FiRefreshCw, FiType, FiLink, FiStar, FiEdit3} = FiIcons

const SiteContentManager = () => {
  const {content, loading, updateContent} = useSiteContent()
  const {trackCTAClick} = useAnalytics()
  const [saving, setSaving] = useState(false)
  const [footerLinks, setFooterLinks] = useState([])
  const [previewMode, setPreviewMode] = useState(false)

  const {register, handleSubmit, setValue, watch, formState: {errors}} = useForm({
    defaultValues: {
      hero_title: 'Sustainable Weight Loss for LIFE',
      hero_subtitle: 'Simple, sustainable habits for people with real lives.',
      cta_primary_label: 'Explore Courses',
      cta_primary_url: '/courses',
      cta_secondary_label: 'Book a Call',
      cta_secondary_url: 'https://tidycal.com/empowernutrition/30-minute-consultation',
      reviews_google_count: 51,
      reviews_source_label: 'Google',
      transformation_cta_label: 'Book a Consultation',
      howitworks_ppc_definition: 'PPC stands for Protein, Produce, and Carbs - the foundations of sustainable nutrition'
    }
  })

  // Load content into form
  useEffect(() => {
    if (content) {
      setValue('hero_title', content.hero_title)
      setValue('hero_subtitle', content.hero_subtitle)
      setValue('cta_primary_label', content.cta_primary_label)
      setValue('cta_primary_url', content.cta_primary_url)
      setValue('cta_secondary_label', content.cta_secondary_label)
      setValue('cta_secondary_url', content.cta_secondary_url)
      setValue('reviews_google_count', content.reviews_google_count)
      setValue('reviews_source_label', content.reviews_source_label)
      setValue('transformation_cta_label', content.transformation_cta_label)
      setValue('howitworks_ppc_definition', content.howitworks_ppc_definition)
      setFooterLinks(content.footer_links || [])
    }
  }, [content, setValue])

  const onSubmit = async (data) => {
    setSaving(true)
    try {
      const contentData = {
        ...data,
        footer_links: footerLinks.filter(link => link.label && link.url)
      }
      
      await updateContent(contentData)
      trackCTAClick('site_content_saved', 'admin_content')
    } catch (error) {
      toast.error('Failed to save content')
    } finally {
      setSaving(false)
    }
  }

  const addFooterLink = () => {
    setFooterLinks(prev => [...prev, {label: '', url: ''}])
    trackCTAClick('footer_link_added', 'admin_content')
  }

  const updateFooterLink = (index, field, value) => {
    setFooterLinks(prev => 
      prev.map((link, i) => 
        i === index ? {...link, [field]: value} : link
      )
    )
  }

  const removeFooterLink = (index) => {
    setFooterLinks(prev => prev.filter((_, i) => i !== index))
    trackCTAClick('footer_link_removed', 'admin_content')
  }

  // Compute reviews display text for preview
  const computeReviewsDisplay = (count, source) => {
    const flooredCount = Math.floor(count / 10) * 10
    const displayCount = flooredCount > 0 ? flooredCount : 50
    return `${displayCount}+ verified reviews on ${source}`
  }

  const breadcrumbItems = [
    {label: 'Admin', href: '/admin'},
    {label: 'Site Content', href: '#'}
  ]

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner size="xl" />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <SEOHead
        title="Site Content Manager | Admin | Empower Nutrition"
        description="Edit all site content including hero text, CTAs, reviews, and footer links"
        url="/admin/content"
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <AdminBreadcrumb items={breadcrumbItems} />

        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Site Content Manager</h1>
          <p className="text-gray-600 mt-2">
            Edit all public-facing content without code changes
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Content Form */}
          <div className="space-y-8">
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
              {/* Hero Section */}
              <motion.div
                initial={{opacity: 0, y: 20}}
                animate={{opacity: 1, y: 0}}
                className="bg-white rounded-lg border border-gray-200 p-6"
              >
                <div className="flex items-center space-x-2 mb-6">
                  <SafeIcon icon={FiType} className="w-5 h-5 text-blue-600" />
                  <h2 className="text-xl font-bold text-gray-900">Hero Section</h2>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Hero Title *
                    </label>
                    <input
                      {...register('hero_title', {required: 'Hero title is required'})}
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-nutrition-500 focus:border-nutrition-500"
                      placeholder="Sustainable Weight Loss for LIFE"
                    />
                    {errors.hero_title && (
                      <p className="mt-1 text-sm text-red-600">{errors.hero_title.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Hero Subtitle *
                    </label>
                    <textarea
                      {...register('hero_subtitle', {required: 'Hero subtitle is required'})}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-nutrition-500 focus:border-nutrition-500"
                      placeholder="Simple, sustainable habits for people with real lives."
                    />
                    {errors.hero_subtitle && (
                      <p className="mt-1 text-sm text-red-600">{errors.hero_subtitle.message}</p>
                    )}
                  </div>
                </div>
              </motion.div>

              {/* Call-to-Action Buttons */}
              <motion.div
                initial={{opacity: 0, y: 20}}
                animate={{opacity: 1, y: 0}}
                transition={{delay: 0.1}}
                className="bg-white rounded-lg border border-gray-200 p-6"
              >
                <div className="flex items-center space-x-2 mb-6">
                  <SafeIcon icon={FiLink} className="w-5 h-5 text-green-600" />
                  <h2 className="text-xl font-bold text-gray-900">Call-to-Action Buttons</h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <h3 className="font-medium text-gray-900">Primary CTA</h3>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Button Label *
                      </label>
                      <input
                        {...register('cta_primary_label', {required: 'Primary CTA label is required'})}
                        type="text"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-nutrition-500 focus:border-nutrition-500"
                        placeholder="Explore Courses"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Button URL *
                      </label>
                      <input
                        {...register('cta_primary_url', {required: 'Primary CTA URL is required'})}
                        type="text"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-nutrition-500 focus:border-nutrition-500"
                        placeholder="/courses"
                      />
                    </div>
                  </div>

                  <div className="space-y-4">
                    <h3 className="font-medium text-gray-900">Secondary CTA</h3>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Button Label *
                      </label>
                      <input
                        {...register('cta_secondary_label', {required: 'Secondary CTA label is required'})}
                        type="text"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-nutrition-500 focus:border-nutrition-500"
                        placeholder="Book a Call"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Button URL *
                      </label>
                      <input
                        {...register('cta_secondary_url', {
                          required: 'Secondary CTA URL is required',
                          pattern: {
                            value: /^(https?:\/\/|\/)/,
                            message: 'Please enter a valid URL'
                          }
                        })}
                        type="url"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-nutrition-500 focus:border-nutrition-500"
                        placeholder="https://tidycal.com/empowernutrition/30-minute-consultation"
                      />
                      {errors.cta_secondary_url && (
                        <p className="mt-1 text-sm text-red-600">{errors.cta_secondary_url.message}</p>
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Reviews Section */}
              <motion.div
                initial={{opacity: 0, y: 20}}
                animate={{opacity: 1, y: 0}}
                transition={{delay: 0.2}}
                className="bg-white rounded-lg border border-gray-200 p-6"
              >
                <div className="flex items-center space-x-2 mb-6">
                  <SafeIcon icon={FiStar} className="w-5 h-5 text-yellow-600" />
                  <h2 className="text-xl font-bold text-gray-900">Reviews & Social Proof</h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Google Reviews Count *
                    </label>
                    <input
                      {...register('reviews_google_count', {
                        required: 'Reviews count is required',
                        valueAsNumber: true,
                        min: {value: 1, message: 'Must be at least 1'}
                      })}
                      type="number"
                      min="1"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-nutrition-500 focus:border-nutrition-500"
                      placeholder="51"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Will be displayed as: {computeReviewsDisplay(watch('reviews_google_count') || 51, watch('reviews_source_label') || 'Google')}
                    </p>
                    {errors.reviews_google_count && (
                      <p className="mt-1 text-sm text-red-600">{errors.reviews_google_count.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Review Source *
                    </label>
                    <input
                      {...register('reviews_source_label', {required: 'Review source is required'})}
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-nutrition-500 focus:border-nutrition-500"
                      placeholder="Google"
                    />
                    {errors.reviews_source_label && (
                      <p className="mt-1 text-sm text-red-600">{errors.reviews_source_label.message}</p>
                    )}
                  </div>
                </div>
              </motion.div>

              {/* Content Definitions */}
              <motion.div
                initial={{opacity: 0, y: 20}}
                animate={{opacity: 1, y: 0}}
                transition={{delay: 0.3}}
                className="bg-white rounded-lg border border-gray-200 p-6"
              >
                <div className="flex items-center space-x-2 mb-6">
                  <SafeIcon icon={FiEdit3} className="w-5 h-5 text-purple-600" />
                  <h2 className="text-xl font-bold text-gray-900">Content Definitions</h2>
                </div>

                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Transformation CTA Label *
                    </label>
                    <input
                      {...register('transformation_cta_label', {required: 'Transformation CTA label is required'})}
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-nutrition-500 focus:border-nutrition-500"
                      placeholder="Book a Consultation"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Used in "Ready to Start Your Transformation" section
                    </p>
                    {errors.transformation_cta_label && (
                      <p className="mt-1 text-sm text-red-600">{errors.transformation_cta_label.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      PPC Method Definition *
                    </label>
                    <textarea
                      {...register('howitworks_ppc_definition', {required: 'PPC definition is required'})}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-nutrition-500 focus:border-nutrition-500"
                      placeholder="PPC stands for Protein, Produce, and Carbs - the foundations of sustainable nutrition"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Used in How It Works section and throughout the site
                    </p>
                    {errors.howitworks_ppc_definition && (
                      <p className="mt-1 text-sm text-red-600">{errors.howitworks_ppc_definition.message}</p>
                    )}
                  </div>
                </div>
              </motion.div>

              {/* Footer Links */}
              <motion.div
                initial={{opacity: 0, y: 20}}
                animate={{opacity: 1, y: 0}}
                transition={{delay: 0.4}}
                className="bg-white rounded-lg border border-gray-200 p-6"
              >
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center space-x-2">
                    <SafeIcon icon={FiLink} className="w-5 h-5 text-indigo-600" />
                    <h2 className="text-xl font-bold text-gray-900">Footer Links</h2>
                  </div>
                  <button
                    type="button"
                    onClick={addFooterLink}
                    className="flex items-center space-x-2 text-nutrition-600 hover:text-nutrition-700 font-medium"
                  >
                    <SafeIcon icon={FiPlus} className="w-4 h-4" />
                    <span>Add Link</span>
                  </button>
                </div>

                <div className="space-y-4">
                  {footerLinks.map((link, index) => (
                    <motion.div
                      key={index}
                      initial={{opacity: 0, y: 20}}
                      animate={{opacity: 1, y: 0}}
                      className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg"
                    >
                      <input
                        type="text"
                        value={link.label}
                        onChange={(e) => updateFooterLink(index, 'label', e.target.value)}
                        placeholder="Link Label"
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-nutrition-500 focus:border-nutrition-500"
                      />
                      <input
                        type="text"
                        value={link.url}
                        onChange={(e) => updateFooterLink(index, 'url', e.target.value)}
                        placeholder="/page-url or https://external.com"
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-nutrition-500 focus:border-nutrition-500"
                      />
                      <button
                        type="button"
                        onClick={() => removeFooterLink(index)}
                        className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                      >
                        <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                      </button>
                    </motion.div>
                  ))}

                  {footerLinks.length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                      <p>No footer links added yet</p>
                      <button
                        type="button"
                        onClick={addFooterLink}
                        className="mt-2 text-nutrition-600 hover:text-nutrition-700 font-medium"
                      >
                        Add your first footer link
                      </button>
                    </div>
                  )}
                </div>

                {/* Suggested Footer Links */}
                <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <h4 className="font-medium text-blue-900 mb-2">Suggested Footer Links:</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-blue-800">
                    <div>• Privacy Policy (/privacy)</div>
                    <div>• Terms of Service (/terms)</div>
                    <div>• Contact (mailto:laura@empowernutrition.info)</div>
                    <div>• About (/about)</div>
                    <div>• Courses (/courses)</div>
                    <div>• Blog (/blog)</div>
                  </div>
                </div>
              </motion.div>

              {/* Action Buttons */}
              <div className="flex items-center justify-between">
                <button
                  type="button"
                  onClick={() => setPreviewMode(!previewMode)}
                  className="flex items-center space-x-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
                >
                  <SafeIcon icon={FiEye} className="w-4 h-4" />
                  <span>{previewMode ? 'Hide Preview' : 'Show Preview'}</span>
                </button>

                <div className="flex items-center space-x-3">
                  <button
                    type="button"
                    onClick={() => window.location.reload()}
                    className="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                  >
                    <SafeIcon icon={FiRefreshCw} className="w-4 h-4" />
                    <span>Reset</span>
                  </button>

                  <button
                    type="submit"
                    disabled={saving}
                    className="flex items-center space-x-2 bg-nutrition-600 hover:bg-nutrition-700 text-white px-6 py-3 rounded-md font-semibold transition-colors disabled:opacity-50"
                  >
                    {saving ? (
                      <LoadingSpinner size="sm" />
                    ) : (
                      <SafeIcon icon={FiSave} className="w-4 h-4" />
                    )}
                    <span>{saving ? 'Saving...' : 'Save Changes'}</span>
                  </button>
                </div>
              </div>
            </form>
          </div>

          {/* Live Preview Panel */}
          <div className="space-y-6">
            <motion.div
              initial={{opacity: 0, x: 20}}
              animate={{opacity: 1, x: 0}}
              className="bg-white rounded-lg border border-gray-200 p-6 sticky top-8"
            >
              <div className="flex items-center space-x-2 mb-6">
                <SafeIcon icon={FiEye} className="w-5 h-5 text-gray-600" />
                <h3 className="text-lg font-bold text-gray-900">Live Preview</h3>
              </div>

              <div className="space-y-6">
                {/* Hero Preview */}
                <div className="p-4 bg-gradient-to-br from-nutrition-50 to-white rounded-lg border">
                  <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">Hero Section</h4>
                  <h1 className="text-2xl font-bold text-gray-900 mb-2 leading-tight">
                    {watch('hero_title') || 'Hero Title'}
                  </h1>
                  <p className="text-gray-600 mb-4">
                    {watch('hero_subtitle') || 'Hero subtitle...'}
                  </p>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <div className="bg-nutrition-600 text-white px-4 py-2 rounded text-sm text-center">
                      {watch('cta_primary_label') || 'Primary CTA'}
                    </div>
                    <div className="border border-nutrition-600 text-nutrition-600 px-4 py-2 rounded text-sm text-center">
                      {watch('cta_secondary_label') || 'Secondary CTA'}
                    </div>
                  </div>
                </div>

                {/* Reviews Preview */}
                <div className="p-4 bg-gray-50 rounded-lg border">
                  <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">Reviews Chip</h4>
                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                    <SafeIcon icon={FiStar} className="w-4 h-4 text-yellow-400" />
                    <span>
                      {computeReviewsDisplay(
                        watch('reviews_google_count') || 51,
                        watch('reviews_source_label') || 'Google'
                      )}
                    </span>
                  </div>
                </div>

                {/* PPC Definition Preview */}
                <div className="p-4 bg-nutrition-50 rounded-lg border">
                  <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">PPC Method</h4>
                  <div className="text-sm text-gray-700">
                    <strong>Step 1: PPC Method</strong>
                    <p className="mt-1">
                      {watch('howitworks_ppc_definition') || 'PPC definition...'}
                    </p>
                  </div>
                </div>

                {/* Transformation CTA Preview */}
                <div className="p-4 bg-gray-50 rounded-lg border">
                  <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">Transformation Section</h4>
                  <div className="space-y-2">
                    <div className="bg-nutrition-600 text-white px-3 py-2 rounded text-sm text-center">
                      View Our Courses
                    </div>
                    <div className="border border-nutrition-600 text-nutrition-600 px-3 py-2 rounded text-sm text-center">
                      {watch('transformation_cta_label') || 'Transformation CTA'}
                    </div>
                  </div>
                </div>

                {/* Footer Links Preview */}
                <div className="p-4 bg-gray-900 text-white rounded-lg">
                  <h4 className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-3">Footer Links</h4>
                  <div className="space-y-2">
                    {footerLinks.filter(link => link.label && link.url).map((link, index) => (
                      <div key={index} className="text-sm text-gray-300">
                        • {link.label}
                      </div>
                    ))}
                    {footerLinks.filter(link => link.label && link.url).length === 0 && (
                      <p className="text-sm text-gray-500 italic">No footer links configured</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <h4 className="font-medium text-gray-900 mb-3">Quick Actions</h4>
                <div className="space-y-2">
                  <a
                    href="/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center space-x-2 text-nutrition-600 hover:text-nutrition-700 text-sm"
                  >
                    <SafeIcon icon={FiEye} className="w-4 h-4" />
                    <span>Preview Homepage</span>
                  </a>
                  <a
                    href="/courses"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center space-x-2 text-nutrition-600 hover:text-nutrition-700 text-sm"
                  >
                    <SafeIcon icon={FiEye} className="w-4 h-4" />
                    <span>Preview Courses Page</span>
                  </a>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SiteContentManager